import { loadCatalog, rideOr } from '@runkr/config/catalog'
import type { CatalogRide, RideId } from '@runkr/config/catalog'
import rigsJson from '../game/rigs.json'
import runnersJson from '../game/runners.json'
import spriteManifest from '../game/spriteManifest.json'
import { Store } from '../net/store'
import { pickRunner, runnerSlots } from '../ui/runnerPreview/roster'
import type { RunnerSlot } from '../ui/runnerPreview/roster'

/**
 * The player's cosmetic choices (D-049), kept in localStorage `runkr.cosmetics`. T3.1 stores the
 * runner (`charId`, sent on every join); T3.8 adds the costume slots to the same object behind
 * the `CosmeticsStore` interface, and a Phase 2 server store plugs in here later (O-21, D-053).
 * T3.11 adds the equipped ride (`ride`, sent on every join as `options.ride`).
 */
export const COSMETICS_KEY = 'runkr.cosmetics'

/** Storage that may be missing or throw (private mode, blocked site data). */
export type MaybeStorage = Pick<Storage, 'getItem' | 'setItem'> | null

export interface Cosmetics {
  /** The equipped runner, always a slot that can be worn (or 0 with no art at all). */
  readonly charId: Store<number>
  readonly runners: readonly RunnerSlot[]
  /** Equip a runner; slots without art are ignored. */
  setCharId(charId: number): void
  /** The equipped ride (T3.11): always a ride the player owns; the catalogue default at first. */
  readonly equippedRide: Store<RideId>
  /** The catalogue's rides in display order. */
  readonly rides: readonly CatalogRide[]
  /** Owned rides can be equipped: starters and free ones (no shop yet, T3.9 adds prices). */
  ownsRide(id: RideId): boolean
  /** Equip a ride; unknown or locked ids are ignored. Ends a try-on. */
  setRide(id: RideId): void
  /**
   * A ride tried on in the shop (T3.9a): the hub shows it instead of the equipped one. It is
   * never stored or sent with a join, and is cleared when the player leaves the hub for a race.
   */
  readonly tryOnRide: Store<RideId | null>
}

const CATALOG = loadCatalog()

function readAll(storage: MaybeStorage): Record<string, unknown> {
  try {
    const raw = storage?.getItem(COSMETICS_KEY)
    const v: unknown = raw ? JSON.parse(raw) : null
    return typeof v === 'object' && v !== null && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

/** Write one key, keeping whatever else is stored (T3.8's slots, the runner, the ride). */
function writeKey(storage: MaybeStorage, key: string, value: unknown): void {
  try {
    storage?.setItem(COSMETICS_KEY, JSON.stringify({ ...readAll(storage), [key]: value }))
  } catch {
    // No storage: the choice lasts for this page only.
  }
}

export function createCosmetics(
  storage: MaybeStorage,
  runners: readonly RunnerSlot[],
  rides: readonly CatalogRide[] = CATALOG.rides,
): Cosmetics {
  const stored = readAll(storage)
  const charId = new Store(
    pickRunner(runners, typeof stored.charId === 'number' ? stored.charId : -1),
  )
  const ownsRide = (id: RideId): boolean =>
    rides.some((r) => r.id === id && (r.starter === true || r.price === 0))
  const fallback = rides.find((r) => r.starter)?.id ?? CATALOG.defaultRide
  const storedRide = rideOr(stored.ride, fallback)
  const equippedRide = new Store<RideId>(ownsRide(storedRide) ? storedRide : fallback)
  const tryOnRide = new Store<RideId | null>(null)
  return {
    charId,
    runners,
    setCharId(id: number): void {
      if (!runners.some((r) => r.charId === id && r.ready)) return
      charId.set(id)
      writeKey(storage, 'charId', id)
    },
    equippedRide,
    rides,
    ownsRide,
    setRide(id: RideId): void {
      if (!ownsRide(id)) return
      equippedRide.set(id)
      tryOnRide.set(null)
      writeKey(storage, 'ride', id)
    },
    tryOnRide,
  }
}

function browserStorage(): MaybeStorage {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** The cast from the art data (`spriteManifest.json`, `rigs.json`, `runners.json`). */
export const RUNNERS: readonly RunnerSlot[] = runnerSlots(spriteManifest, rigsJson, runnersJson)

/** The page's cosmetics, shared by the hub and the join options. */
export const cosmetics: Cosmetics = createCosmetics(browserStorage(), RUNNERS)
